/**
 * EstimateBriefChat Component (TASK-098)
 *
 * Displays estimate brief-chat messages in a messenger-like UI.
 * User messages on the right (blue), assistant messages on the left (gray).
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { estimatesApi } from '@/services/api/estimates';
import { MessageBubble } from '@/components/messages/MessageBubble';
import { channelShortLabel } from '@/lib/messageChannel';
import type { EstimateMessage } from '@/types/estimate';

interface EstimateBriefChatProps {
  estimateId: string;
  messages: EstimateMessage[];
}

function formatTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function EstimateBriefChat({ estimateId, messages }: EstimateBriefChatProps) {
  const [inputValue, setInputValue] = useState('');
  const [optimisticMessages, setOptimisticMessages] = useState<EstimateMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const allMessages = [...messages, ...optimisticMessages];

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [allMessages.length]);

  const sendMutation = useMutation({
    mutationFn: (content: string) => estimatesApi.sendMessage(estimateId, content),
    onMutate: (content) => {
      // Optimistic: add user message immediately
      const optimisticMsg: EstimateMessage = {
        id: `optimistic-${Date.now()}`,
        role: 'user',
        content,
        source: 'web',
        created_at: new Date().toISOString(),
      };
      setOptimisticMessages(prev => [...prev, optimisticMsg]);
    },
    onSuccess: () => {
      // Clear optimistic messages and refresh data
      setOptimisticMessages([]);
      queryClient.invalidateQueries({ queryKey: ['estimate', estimateId] });
    },
    onError: () => {
      // Remove optimistic messages on error
      setOptimisticMessages([]);
    },
  });

  const handleSend = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed || sendMutation.isPending) return;
    setInputValue('');
    sendMutation.mutate(trimmed);
  }, [inputValue, sendMutation]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  return (
    <Card data-testid="brief-chat">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Диалог</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {/* Messages area */}
        <div
          data-testid="chat-messages"
          className="px-4 py-2 space-y-3 max-h-96 overflow-y-auto"
        >
          {allMessages.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">
              Нет сообщений
            </p>
          ) : (
            allMessages.map((msg) => (
              <MessageBubble
                key={msg.id}
                testId={`message-${msg.role}`}
                message={msg}
                channel={<span>{channelShortLabel(msg.source)}</span>}
                time={formatTime(msg.created_at)}
                captionClassName="gap-1"
              />
            ))
          )}
          {sendMutation.isPending && (
            <div data-testid="chat-loading" className="flex justify-start">
              <div className="bg-gray-100 rounded-lg px-3 py-2 text-sm text-gray-500 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                AI обрабатывает запрос...
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="border-t px-4 py-3 flex gap-2">
          <Textarea
            data-testid="chat-input"
            placeholder="Напишите сообщение... (Ctrl+Enter для отправки)"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
            className="resize-none"
            disabled={sendMutation.isPending}
          />
          <Button
            data-testid="chat-send-button"
            onClick={handleSend}
            disabled={!inputValue.trim() || sendMutation.isPending}
            size="icon"
            className="shrink-0 self-end"
          >
            {sendMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
